/** The show sheet, and the Log / Edit sheets' draft: what they fetch, send and check. */
import { getJson, send } from "./request";
import type { Catalog, Draft, MediaItem, Person, ShowDetail, WentPrompt } from "./types";

export const fetchShow = (id: number) => getJson<ShowDetail>(`/api/shows/${id}`, "Could not load the show");
export const fetchCatalog = () => getJson<Catalog>("/api/catalog", "Could not load bands and venues");
export const fetchPeople = async () => (await getJson<{ people: Person[] }>("/api/people", "Could not load people")).people;

interface ServerDraft {
  date: string;
  venue_name: string;
  venue_location: string | null;
  event_name: string | null;
  bands: string[];
  image_url: string | null;
  import_upcoming_id: number;
}

/** Log it: the server splits the listing's bill and matches names. */
export async function draftFromUpcoming(upcomingId: number): Promise<Draft> {
  const d = await getJson<ServerDraft>(`/api/shows/draft/${upcomingId}`, "Could not load that listing");
  return {
    date: d.date,
    venue_name: d.venue_name,
    venue_location: d.venue_location ?? "",
    event_name: d.event_name ?? "",
    bands: d.bands,
    image_url: d.image_url,
    person_ids: [],
    solo: false,
    import_upcoming_id: d.import_upcoming_id,
  };
}

/** Log a show's "From Upcoming" picker: listings RSVP'd Going from today back, not yet logged, newest first. */
export const fetchLoggable = async () =>
  (await getJson<{ listings: WentPrompt[] }>("/api/shows/loggable", "Could not load Upcoming listings")).listings;

export const blankDraft = (today: string): Draft => ({
  date: today,
  venue_name: "",
  venue_location: "",
  event_name: "",
  bands: [],
  image_url: null,
  person_ids: [],
  solo: false,
  import_upcoming_id: null,
});

export const draftFromShow = (s: ShowDetail): Draft => ({
  date: s.date,
  venue_name: s.venue.name,
  venue_location: s.venue.location ?? "",
  event_name: s.event ?? "",
  bands: s.bill.map((b) => b.name),
  image_url: s.image_url,
  person_ids: s.people.map((p) => p.id),
  solo: s.solo,
  import_upcoming_id: null,
});

/** Append a band unless it is blank or already on the bill (any case). */
export function addBand(bands: string[], name: string): string[] {
  const n = name.trim();
  if (!n || bands.some((b) => b.toLowerCase() === n.toLowerCase())) return bands;
  return [...bands, n];
}

export function moveItem<T>(items: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= items.length) return items;
  const next = [...items];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export const removeAt = <T,>(items: T[], i: number): T[] => items.filter((_, k) => k !== i);

export const toggle = (ids: number[], id: number) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

export const knownVenue = (c: Catalog, name: string) => c.venues.find((v) => v.name.toLowerCase() === name.trim().toLowerCase());

/** The catalog's spelling of a band already in it, or undefined. */
export const knownBand = (c: Catalog, name: string) => c.bands.find((b) => b.toLowerCase() === name.trim().toLowerCase());

/**
 * The bands on this bill that the catalog has never seen, in billing order.
 * Saving mints a `bands` row for each, so the sheet marks them before it does:
 * a flyer reading or an Upcoming listing can name a band nobody has matched.
 */
export const newBands = (c: Catalog, bands: string[]) => bands.filter((b) => b.trim() && !knownBand(c, b));

/** What the sheet says above an imported bill: how much of it the show would add. */
export function newBandsNote(total: number, fresh: number): string {
  if (!fresh) return "Every band here is already in your bands.";
  if (fresh === total) return total === 1 ? "This band is new to your bands." : `All ${total} bands are new to your bands.`;
  // fresh is below total here, so there are at least two bands.
  return `${fresh} of ${total} bands ${fresh === 1 ? "is" : "are"} new to your bands.`;
}

/** Why the draft cannot be saved yet, or null. The server checks the same rules. */
export function draftProblem(d: Draft, c: Catalog): string | null {
  if (!d.bands.length) return "Add at least one band.";
  if (!d.venue_name.trim()) return "Pick a venue.";
  if (!knownVenue(c, d.venue_name) && !d.venue_location.trim()) return "A new venue needs an address.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date)) return "Pick a date.";
  return null;
}

function body(d: Draft, c: Catalog) {
  const known = knownVenue(c, d.venue_name);
  return {
    date: d.date,
    // The catalog's spelling, so "paradise" does not become a second venue.
    venue_name: known ? known.name : d.venue_name.trim(),
    venue_location: known ? known.location : d.venue_location.trim() || null,
    // A pick for a known venue without one backfills it (catalog_logic.get_or_create_venue).
    venue_place: d.venue_place ?? null,
    event_name: d.event_name.trim() || null,
    // The catalog's spelling, so "pennywise" does not become a second band
    // (get_or_create_band matches the name exactly). It also keeps the new
    // markers honest: what is left unmatched really is new.
    bands: d.bands.map((b) => knownBand(c, b) ?? b.trim()),
    image_url: d.image_url,
    person_ids: d.solo ? [] : d.person_ids,
    solo: d.solo,
  };
}

export const logShow = (d: Draft, c: Catalog) =>
  send<ShowDetail>("/api/shows", "POST", { ...body(d, c), import_upcoming_id: d.import_upcoming_id }, "Could not log the show");
export const saveShow = (id: number, d: Draft, c: Catalog) => send<ShowDetail>(`/api/shows/${id}`, "PUT", body(d, c), "Could not save the show");
export const deleteShow = (id: number) => send(`/api/shows/${id}`, "DELETE", undefined, "Could not delete the show");
export const setAttendees = (id: number, person_ids: number[], solo: boolean) =>
  send<ShowDetail>(`/api/shows/${id}/attendees`, "PUT", { person_ids: solo ? [] : person_ids, solo }, "Could not save who went");

export const MEDIA_TYPES: [string, string][] = [
  ["full_show", "Full show"],
  ["full_set", "Full set"],
  ["song", "Song"],
  ["clip", "Clip"],
  ["other", "Other"],
];
export const mediaTypeLabel = (t: string) => MEDIA_TYPES.find(([v]) => v === t)?.[1] ?? t;

export interface MediaDraft {
  url: string;
  band_id: number | null;
  media_type: string;
  media_format: string;
  title: string;
}
export const blankMedia = (): MediaDraft => ({ url: "", band_id: null, media_type: "full_set", media_format: "video", title: "" });
const mediaBody = (m: MediaDraft) => ({ ...m, url: m.url.trim(), title: m.title.trim() || null });
export const addMedia = (showId: number, m: MediaDraft) => send(`/api/shows/${showId}/media`, "POST", mediaBody(m), "Could not add the link");
export const deleteMedia = (id: number) => send(`/api/media/${id}`, "DELETE", undefined, "Could not remove the link");
export const reorderMedia = (showId: number, items: MediaItem[]) =>
  send(`/api/shows/${showId}/media/order`, "PUT", { ids: items.map((m) => m.id) }, "Could not reorder");

/** One line to paste into Instagram. */
export function shareText(s: ShowDetail, dateLabel: string): string {
  const bill = s.bill.map((b) => b.name).join(", ");
  return `🎸 ${dateLabel}: ${bill} @ ${s.venue.name}${s.event ? ` (${s.event})` : ""}`;
}

export const FLYER_STATUS_TEXT: Record<string, string> = {
  searched_empty: "Searched, none found yet. It is listed on the public Flyers wanted page.",
  no_flyer_exists: "Marked as never having a flyer.",
};
export const flyerStatusText = (status: string | null) =>
  (status && FLYER_STATUS_TEXT[status]) || "Not searched yet. The flyer agent will look for it.";

